import React, { useContext } from "react";
import arrows from "../../assets/images/icons/sidebar/arrows.svg";
import iconDashboard from "../../assets/images/icons/sidebar/dashboard.svg";
import activeIconDashboard from "../../assets/images/icons/sidebar/dashboard_full.svg";
import iconServices from "../../assets/images/icons/sidebar/services.svg";
import activeIconServices from "../../assets/images/icons/sidebar/services_full.svg";
import iconDocuments from "../../assets/images/icons/sidebar/documents.svg";
import activeIconDocuments from "../../assets/images/icons/sidebar/documents_full.svg";
import iconContracts from "../../assets/images/icons/sidebar/contracts.svg";
import activeIconContracts from "../../assets/images/icons/sidebar/contracts_full.svg";
import iconTrips from "../../assets/images/icons/sidebar/trips.svg";
import activeIconTrips from "../../assets/images/icons/sidebar/trips_full.svg";
import iconFleetRoute from "../../assets/images/icons/sidebar/fleet-route.svg";
import activeIconFleetRoute from "../../assets/images/icons/sidebar/fleet-route_full.svg";
import iconConfiguration from "../../assets/images/icons/sidebar/configuration.svg";
import activeIconConfiguration from "../../assets/images/icons/sidebar/configuration_full.svg";
import arceseLogo from "../../assets/images/RedLogo.svg";
import { routeUrls } from "../../config/routeUrls";
import { Link } from "react-router-dom";
import { useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AccountStatusContext } from "../../context/accountStatusContext";
import { IAccountStatusContext } from "../../types/accountStatus";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import { Box } from "@mui/material";
import { useWindowSize } from "../../utils/hooks/useWindowSize";

interface ISidebarEl {
  iconSource: string;
  activeIconSource: string;
  isActive: boolean;
  label: string;
  url: string;
}

interface ISidebarProps {
  isOpenOnMobile?: boolean;
  businessProfile: any;
}

const Sidebar = React.forwardRef<HTMLElement, ISidebarProps>(
  ({ isOpenOnMobile = false, businessProfile }, ref) => {
    const windowsWidth = useWindowSize();
    const isMobile = windowsWidth < 768;
    const { t } = useTranslation();
    const { userHasPartialAccess } = useContext(
      AccountStatusContext
    ) as IAccountStatusContext;
    // Precalcolo i match per tutte le route che servono
    const matchDashboard = useMatch(routeUrls.dashboard);
    const matchTrips = useMatch(routeUrls.trips);
    const matchFleetAndRoute = useMatch(routeUrls.fleetAndRoute);
    const matchDocuments = useMatch(routeUrls.documents);
    const matchServices = useMatch(routeUrls.services);
    const matchContracts = useMatch(routeUrls.contracts);
    const matchSettings = useMatch(routeUrls.settings);
    // Funzione pura
    const CheckIfActive = (match: ReturnType<typeof useMatch>): boolean => {
      return !!match && !!match.pathnameBase;
    };
    const SidebarEl: React.FC<ISidebarEl> = ({
      iconSource,
      activeIconSource,
      isActive,
      label,
      url,
    }) => {
      return (
        <Link
          to={url}
          className={
            "lg:w-20 h-[84px] group-hover:w-full group-hover:justify-start border-l-[3px] hover:bg-grey cursor-pointer py-4 flex items-center justify-center" +
            (isActive ? " border-red" : " border-transparent")
          }
        >
          <div className="w-20 lg:w-[75px] group-hover:lg:w-[75px] relative mr-[3px]">
            <div className="flex justify-center">
              <img
                className="block"
                src={isActive ? activeIconSource : iconSource}
                alt="arrows"
              />
            </div>
            <div
              className={
                "hidden lg:block group-hover:lg:hidden w-full absolute top-full left-1/2 -translate-x-1/2 mt-1 text-[10px] text-center " +
                (isActive ? "text-black" : "text-[#979797]")
              }
            >
              {label}
            </div>
          </div>

          <div
            className={
              "lg:hidden group-hover:lg:block text-xs uppercase" +
              (isActive ? " font-semibold" : "")
            }
          >
            {label}
          </div>
        </Link>
      );
    };

    return (
      <aside
        ref={ref}
        id="sidebar-menu"
        className={
          "transition z-[9999] group absolute left-0 top-0 lg:translate-x-0 h-full w-60 lg:w-20 lg:hover:w-60 flex flex-col items-start bg-white overflow-auto hide-scrollbar" +
          (isOpenOnMobile === false
            ? " -translate-x-[100%]"
            : " sidebar-shadow")
        }
      >
        <div className="hidden lg:flex w-full cursor-pointer p-4 items-center justify-center group-hover:lg:justify-end">
          <img
            className="group-hover:lg:rotate-180"
            src={arrows}
            alt="arrows"
          />
        </div>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', alignItems: 'flex-start', height: '100%', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
            {userHasPartialAccess === false && (
              <SidebarEl
                isActive={CheckIfActive(matchDashboard)}
                iconSource={iconDashboard}
                activeIconSource={activeIconDashboard}
                label={t("common.menu.dashboard")}
                url={routeUrls.dashboard}
              />
            )}

            {businessProfile?.isagency && (
              <SidebarEl
                isActive={CheckIfActive(matchTrips)}
                iconSource={iconTrips}
                activeIconSource={activeIconTrips}
                label={t("common.menu.trips")}
                url={routeUrls.trips}
              />
            )}
            {userHasPartialAccess === false && (
              <SidebarEl
                isActive={CheckIfActive(matchFleetAndRoute)}
                iconSource={iconFleetRoute}
                activeIconSource={activeIconFleetRoute}
                label={t("common.menu.fleetAndRoute")}
                url={routeUrls.fleetAndRoute}
              />
            )}

            {userHasPartialAccess === false && (
              <SidebarEl
                isActive={CheckIfActive(matchDocuments)}
                iconSource={iconDocuments}
                activeIconSource={activeIconDocuments}
                label={t("common.menu.documents")}
                url={routeUrls.documents}
              />
            )}

            {userHasPartialAccess === false && (
              <SidebarEl
                isActive={CheckIfActive(matchServices)}
                iconSource={iconServices}
                activeIconSource={activeIconServices}
                label={t("common.menu.services")}
                url={routeUrls.services}
              />
            )}

            <SidebarEl
              isActive={CheckIfActive(matchContracts)}
              iconSource={iconContracts}
              activeIconSource={activeIconContracts}
              label={t("common.menu.contracts")}
              url={routeUrls.contracts}
            />

            <SidebarEl
              isActive={CheckIfActive(matchSettings)}
              iconSource={iconConfiguration}
              activeIconSource={activeIconConfiguration}
              label={t("common.menu.settings")}
              url={routeUrls.settings}
            />
          </Box>
          {/*DA RIMUOVERE DOPO PROD TECNICO* */}
          {isMobile &&  (
            <SidebarEl
              isActive={false}
              iconSource={arceseLogo}
              activeIconSource={arceseLogo}
              label={t("common.menu.app")}
              url={routeUrls.app}
            />
          )}
        </Box>
      </aside>
    );
  }
);

export default Sidebar;
